/**
 * Shared inline venue-edit modal (Part 2C).
 *
 * Used by:
 *   - ReverseSearchScreen apple_fallback rows ("Edit & Save")
 *   - VenueReviewScreen venue-list rows ("Edit coords/FSQ")
 *
 * Save path chosen:
 *   - `convert_apple_venue_to_owned` RPC when opened from a queue row (it
 *     handles the platform_source flip + queue resolution intent).
 *   - Direct `venues` UPDATE when opened from the venue list (safer — avoids
 *     demoting a foursquare row's platform_source).
 *
 * Apple Place ID is read-only and never editable.
 */
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { toTitleCase } from '../lib/appleRecall';

export type VenueEditBase = {
  id: string;
  venue_name_user_input: string | null;
  latitude: number | null;
  longitude: number | null;
  coords_approximate: boolean | null;
  fsq_place_id: string | null;
  apple_place_id: string | null;
  platform_source: string | null;
};

export type VenueEditModalProps = {
  open: boolean;
  base: VenueEditBase | null;
  /** Optional queue id — if set, save uses convert_apple_venue_to_owned + resolves the queue row. */
  fromQueueId?: string | null;
  onClose: () => void;
  onSaved: () => void;
};

function rpcMissing(message: string, fn: string): boolean {
  const m = message.toLowerCase();
  return m.includes(fn.toLowerCase()) || m.includes('42883') || m.includes('could not find the function');
}

export function VenueEditModal({ open, base, fromQueueId, onClose, onSaved }: VenueEditModalProps) {
  const [name, setName] = useState(base?.venue_name_user_input ?? '');
  const [lat, setLat] = useState(base?.latitude != null ? String(base.latitude) : '');
  const [lng, setLng] = useState(base?.longitude != null ? String(base.longitude) : '');
  const [coordsApprox, setCoordsApprox] = useState(!!base?.coords_approximate);
  const [fsqPlaceId, setFsqPlaceId] = useState(base?.fsq_place_id ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Re-initialize the form whenever the modal (re)opens or a different venue is
  // selected. Keyed on open + base.id so a cancelled edit never leaks its values
  // into the next venue opened.
  useEffect(() => {
    if (!open) return;
    setName(base?.venue_name_user_input ?? '');
    setLat(base?.latitude != null ? String(base.latitude) : '');
    setLng(base?.longitude != null ? String(base.longitude) : '');
    setCoordsApprox(!!base?.coords_approximate);
    setFsqPlaceId(base?.fsq_place_id ?? '');
    setErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, base?.id]);

  if (!open || !base) return null;

  async function handleSave() {
    if (!base) return;
    setBusy(true);
    setErr(null);
    const latNum = Number(lat);
    const lngNum = Number(lng);
    if (!name.trim()) {
      setErr('Name required');
      setBusy(false);
      return;
    }
    if (!Number.isFinite(latNum) || latNum < -90 || latNum > 90) {
      setErr('Latitude must be between -90 and 90');
      setBusy(false);
      return;
    }
    if (!Number.isFinite(lngNum) || lngNum < -180 || lngNum > 180) {
      setErr('Longitude must be between -180 and 180');
      setBusy(false);
      return;
    }
    const fsqId = fsqPlaceId.trim() || null;
    try {
      if (fromQueueId) {
        // Apply the venue change via the admin-guarded convert RPC (it sets name,
        // coords, platform_source, reverse_searched, city). convert_apple_venue_to_owned
        // sets exact admin-entered coords, so coords_approximate is left as-is.
        const { error: rpcErr } = await supabase.rpc('convert_apple_venue_to_owned', {
          p_venue_id: base.id,
          p_owned_name: name.trim(),
          p_owned_lat: latNum,
          p_owned_lng: lngNum,
          p_fsq_place_id: fsqId,
        });
        if (rpcErr) {
          if (rpcMissing(rpcErr.message, 'convert_apple_venue_to_owned')) {
            throw new Error('convert_apple_venue_to_owned RPC is not deployed.');
          }
          throw new Error(rpcErr.message);
        }
        // Resolve the queue row via SECURITY DEFINER RPC (a direct UPDATE silently
        // no-ops under the queue's RLS).
        const { data: resData, error: resErr } = await supabase.rpc(
          'admin_resolve_reverse_search_queue',
          { p_queue_id: fromQueueId },
        );
        if (resErr) {
          if (rpcMissing(resErr.message, 'admin_resolve_reverse_search_queue')) {
            throw new Error('admin_resolve_reverse_search_queue RPC is not deployed yet — run `supabase db push`.');
          }
          throw new Error(resErr.message);
        }
        if (resData && (resData as { ok?: boolean }).ok === false) {
          throw new Error((resData as { error?: string }).error ?? 'Could not resolve queue row');
        }
      } else {
        // Venue editor: a direct venues UPDATE silently no-ops under venues RLS for
        // venues the admin didn't personally log — go through the admin RPC.
        const { data: upData, error: upErr } = await supabase.rpc('admin_update_venue_fields', {
          p_venue_id: base.id,
          p_name: name.trim(),
          p_lat: latNum,
          p_lng: lngNum,
          p_fsq_place_id: fsqId,
          p_coords_approximate: coordsApprox,
        });
        if (upErr) {
          if (rpcMissing(upErr.message, 'admin_update_venue_fields')) {
            throw new Error('admin_update_venue_fields RPC is not deployed yet — run `supabase db push`.');
          }
          throw new Error(upErr.message);
        }
        if (upData && (upData as { ok?: boolean }).ok === false) {
          throw new Error((upData as { error?: string }).error ?? 'Save failed');
        }
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => !busy && onClose()}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-border bg-surface p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-white">Edit venue (coords / FSQ)</h2>
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="text-muted hover:text-white text-xs"
          >
            close
          </button>
        </div>

        <div className="space-y-2">
          <label className="block text-[10px] uppercase tracking-[0.12em] text-mutedDim">Name</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-1 rounded-lg border border-border bg-canvas px-3 py-2 text-sm text-white placeholder:text-mutedDim focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <button
              type="button"
              onClick={() => setName(toTitleCase(name))}
              className="shrink-0 rounded-md border border-border px-3 py-2 text-xs font-medium text-muted hover:text-white"
            >
              Title Case
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="block text-[10px] uppercase tracking-[0.12em] text-mutedDim">Latitude</label>
            <input
              type="text"
              inputMode="decimal"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              className="w-full rounded-lg border border-border bg-canvas px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-[10px] uppercase tracking-[0.12em] text-mutedDim">Longitude</label>
            <input
              type="text"
              inputMode="decimal"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              className="w-full rounded-lg border border-border bg-canvas px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={coordsApprox}
            onChange={(e) => setCoordsApprox(e.target.checked)}
          />
          <span>Coordinates are approximate</span>
        </label>

        <div className="space-y-1">
          <label className="block text-[10px] uppercase tracking-[0.12em] text-mutedDim">FSQ Place ID (optional)</label>
          <input
            type="text"
            value={fsqPlaceId}
            placeholder="leave blank for none"
            onChange={(e) => setFsqPlaceId(e.target.value)}
            className="w-full rounded-lg border border-border bg-canvas px-3 py-2 text-sm text-white placeholder:text-mutedDim focus:outline-none focus:ring-2 focus:ring-accent"
          />
          {fromQueueId ? (
            <p className="text-[11px] text-mutedDim">
              {fsqPlaceId.trim()
                ? 'Will MERGE this stub into that Foursquare venue (deletes the stub, keeps its ratings).'
                : 'Blank → saves as a NEW standalone venue (no merge). To merge into an existing Foursquare row, use “Approve merge → FSQ” or paste its FSQ Place ID here.'}
            </p>
          ) : null}
        </div>

        <div className="space-y-1">
          <label className="block text-[10px] uppercase tracking-[0.12em] text-mutedDim">Apple Place ID (read-only)</label>
          <div className="font-mono text-[11px] text-mutedDim break-all rounded-lg border border-border/60 bg-canvas px-3 py-2">
            {base.apple_place_id || '—'}
          </div>
        </div>

        {err ? (
          <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {err}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-md border border-border bg-white/[0.04] px-3 py-1.5 text-xs font-semibold text-muted hover:text-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSave()}
            className="rounded-md bg-accent px-4 py-1.5 text-xs font-semibold text-white hover:opacity-95 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
